import React from "react";
// ProPredict AI Module

// Default
import { useEffect, useRef, useState } from "react";
import Lottie from "react-lottie";

// Mui
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Skeleton from "@mui/material/Skeleton";

// Custom
import CustomToast from "../toast/CustomToast";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import { ToastState } from "../../types";

// React-icons
import { IoMdArrowRoundBack } from "react-icons/io";

// Firebase
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../components/firebase/index";

// Utils
import { fetchGETRequest } from "../../utils/services";

// Animation Icons
import SearchAnimation from "../../assets/lordIcons/wired-outline-1725-exit-sign-hover-pinch.json";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface ForecastingOption {
  value: number;
  label: string;
}

interface ProPredictAIProps {
  openAI: boolean;
  setOpenAI: (open: boolean) => void;
}

const ProPredictAI: React.FC<ProPredictAIProps> = ({ openAI, setOpenAI }) => {
  const [foreCastingOptions, setForeCastingOptions] = useState<
    ForecastingOption[]
  >([
    { value: 2, label: "2 Hour Forecast" },
    { value: 4, label: "4 Hour Forecast" },
    { value: 6, label: "6 Hour Forecast" },
    { value: 8, label: "8 Hour Forecast" },
  ]);

  const [radioCheck, setRadioCheck] = useState<string>("dataset");
  const [foreCastingValue, setForeCastingValue] = useState<number | null>(null);
  const [progressLoader, setProgressLoader] = useState<boolean>(false);
  const [CSVUrl, setCSVUrl] = useState<string | null>(null);
  const [decodedData, setDecodedData] = useState<string | null>(null);
  const [runningPrediction, setRunningPrediction] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });

  const playerRef = useRef<any>(null);
  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    playerRef.current?.playFromBeginning();
  }, []);

  useEffect(() => {
    if (decodedData) {
      if (elRef.current) {
        const firstChild = elRef.current.firstElementChild as HTMLElement;
        if (firstChild) {
          firstChild.style.width = "560px";
        }
      }
    }
  }, [decodedData]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProgressLoader(true);
      uploadFile(file);
    }
  };

  const startPrediction = async () => {
    if (foreCastingValue) {
      setRunningPrediction(true);
      const response = await fetchGETRequest(
        `ML/DemandForecastingPlot?datasource=${CSVUrl}&forecastWindow=${foreCastingValue}`
      );
      const { data } = response;
      if (response) {
        const base64Prefix = "image/svg+xml;base64,";
        const csvBase64 = data.startsWith(base64Prefix)
          ? data.split(",")[1]
          : data;

        const newData = atob(csvBase64);
        setDecodedData(newData);
        setRunningPrediction(false);
      }
    } else {
      setToast({
        message: "Please select a forecasting window.",
        severity: "error",
      });
    }
  };

  const uploadFile = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `files/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        (error) => {
          setProgressLoader(false);
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setProgressLoader(false);
            setToast({
              message:
                "CSV file uploaded successfully! Starting the prediction process. Please wait while we analyze your data.",
              severity: "success",
            });
            setCSVUrl(downloadURL);
            resolve();
          });
        }
      );
    });
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: SearchAnimation,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <Drawer
      open={openAI}
      onClose={() => setOpenAI(false)}
      anchor={"right"}
      role="presentation"
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          boxShadow: "none",
          width: "600px",
          padding: "0px",
        },
      }}
    >
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />
      <Box sx={{ padding: "15px" }}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          mb={"20px"}
        >
          <UnifiedIcon
            icon={IoMdArrowRoundBack}
            fontSize="30px"
            onClick={() => setOpenAI(false)}
          />
          {runningPrediction && (
            <Stack direction={"row"} gap={"7px"} alignItems={"center"}>
              <Lottie options={defaultOptions} height={30} width={30} />
              <Typography variant="caption" fontWeight={600}>
                Running...
              </Typography>
            </Stack>
          )}
        </Stack>

        <Typography variant="h5">PrePredict AI Insights Dashboard</Typography>
        <Typography variant="body1" mt={"20px"} mb={"20px"} fontWeight={500}>
          Choose Your Forecasting Option
        </Typography>
        <Typography variant="caption">
          Upload your CSV file or use our preloaded energy consumption dataset
          for accurate predictions
        </Typography>
        <Stack mt={"10px"}>
          <FormControlLabel
            value="csvfile"
            control={<Radio />}
            checked={radioCheck === "csvfile"}
            onChange={(event) => {
              setRadioCheck((event.target as HTMLInputElement).value);
            }}
            label="Upload your CSV file"
          />
          <FormControlLabel
            value="dataset"
            checked={radioCheck === "dataset"}
            onChange={(event) => {
              setRadioCheck((event.target as HTMLInputElement).value);
            }}
            control={<Radio />}
            label="Use default dataset from repository"
          />
        </Stack>
        <input
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        {radioCheck === "csvfile" && (
          <ContainedButton
            label={"Upload CSV"}
            loading={progressLoader}
            onClick={handleButtonClick}
            sx={{
              width: "100%",
              mt: "20px",
              fontWeight: 600,
              color: "#ffffff",
            }}
          />
        )}
        <Stack mt={"30px"}>
          <CustomOutlinedSelect
            label={"Select Forecasting Window"}
            options={foreCastingOptions}
            onChange={(option) => {
              setForeCastingValue(option.value as number);
            }}
          />
          <ContainedButton
            label={"Start Prediction"}
            onClick={startPrediction}
            sx={{
              width: "100%",
              mt: "20px",
              fontWeight: 600,
              color: "#ffffff",
            }}
          />
        </Stack>
        {decodedData && (
          <div
            ref={elRef}
            style={{ marginTop: 10 }}
            dangerouslySetInnerHTML={{ __html: decodedData }}
          />
        )}

        {runningPrediction && (
          <Box mt={"20px"}>
            <Skeleton
              variant="rectangular"
              width={"100%"}
              height={200}
              sx={{ borderRadius: "5px" }}
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ProPredictAI;
